/**
 * Быстрая модерация: объявления из очереди по одному, крупные фото, подсказки и решения с клавиатуры.
 *   A — одобрить · R — отклонить (1–7 причина, Enter — отправить) · S или → — пропустить · ← — предыдущее
 *   [ ] — листать фото · F — фото на весь экран · ? — подсказка по клавишам · Esc — отмена
 * Каждое решение проходит через обычные эндпоинты модерации (журнал, уведомление продавцу, права — на сервере).
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, errorText, qs, type Paged } from '../api';
import { Icon } from '../icons';
import { Btn, ErrorBox, Loading, Modal, REJECT_REASONS, STATUS_RU, fmtDate } from '../ui';
import { SignalList, type ListingFlags, type ModerationSignals } from './moderation';

interface QueueRow { id: string; title: string; submittedAt: string | null }
interface Detail {
  id: string; status: string; title: string; description: string; price: { formatted: string }; condition: string | null;
  category: string | null; location: string | null; flags: ListingFlags | null;
  images: { id: string; url: string; thumbUrl: string | null }[];
  attributes: { key: string; label: string; value: unknown }[];
  user: { id: string; firstName: string; username: string | null; status: string };
  reports: { id: string; reason: string; comment: string | null; status: string; at: string }[];
  history: { action: string; at: string }[];
  signals?: ModerationSignals;
}

const REASON_KEYS = Object.keys(REJECT_REASONS);
const isTyping = (t: EventTarget | null) => t instanceof HTMLElement && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable);

export function QueuePage() {
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['queue-ids'],
    queryFn: () => api<Paged<QueueRow>>(`/admin/listings${qs({ status: 'pending', page: 1, pageSize: 100 })}`),
    refetchInterval: 30_000,
  });
  const [skipped, setSkipped] = useState<Set<string>>(new Set());
  const [doneIds, setDoneIds] = useState<Set<string>>(new Set());
  const [index, setIndex] = useState(0);
  const [stats, setStats] = useState({ approved: 0, rejected: 0, started: Date.now() });
  const queue = useMemo(() => (list.data?.items ?? []).filter((r) => !doneIds.has(r.id)), [list.data, doneIds]);
  const ordered = useMemo(() => [...queue.filter((r) => !skipped.has(r.id)), ...queue.filter((r) => skipped.has(r.id))], [queue, skipped]);
  const safeIndex = Math.min(index, Math.max(ordered.length - 1, 0));
  const current = ordered[safeIndex];
  const total = list.data?.total ?? 0;
  const decided = stats.approved + stats.rejected;

  // Следующее объявление загружается заранее — переход без ожидания.
  useEffect(() => {
    const next = ordered[safeIndex + 1];
    if (next && qc.getQueryData(['admin-listing', next.id]) === undefined) {
      void api<Detail>(`/admin/listings/${next.id}`).then((d) => qc.setQueryData(['admin-listing', next.id], d), () => undefined);
    }
  }, [ordered, safeIndex, qc]);

  const onDecided = useCallback(
    (id: string, kind: 'approve' | 'reject') => {
      setDoneIds((s) => new Set(s).add(id));
      setStats((s) => ({ ...s, [kind === 'approve' ? 'approved' : 'rejected']: s[kind === 'approve' ? 'approved' : 'rejected'] + 1 }));
      void qc.invalidateQueries({ queryKey: ['stats'] });
      void qc.invalidateQueries({ queryKey: ['admin-listings'] });
    },
    [qc],
  );

  const avgSec = decided ? Math.round((Date.now() - stats.started) / 1000 / decided) : null;

  return (
    <div>
      <div className="page-head">
        <div>
          <h1>Быстрая модерация</h1>
          <div className="queue-progress">
            <span>
              В очереди <b>{Math.max(total - decided, ordered.length)}</b> · за сессию одобрено {stats.approved}, отклонено {stats.rejected}
              {avgSec !== null ? ` · ~${avgSec} с на объявление` : ''}
            </span>
            {total ? (
              <div className="progress" aria-hidden="true">
                <div style={{ width: `${Math.min(100, (decided / Math.max(total, 1)) * 100)}%` }} />
              </div>
            ) : null}
          </div>
        </div>
        <div className="row" style={{ marginTop: 0 }}>
          <Link to="/moderation" className="btn btn-secondary">
            <Icon name="list" size={16} /> Таблицей
          </Link>
        </div>
      </div>
      {list.isLoading ? <Loading /> : list.error ? <ErrorBox error={list.error} onRetry={() => void list.refetch()} /> : !current ? (
        <div className="panel" style={{ textAlign: 'center', padding: 40 }}>
          <h2>Очередь пуста</h2>
          <p className="muted">Новые объявления появятся здесь автоматически — список обновляется каждые 30 секунд.</p>
        </div>
      ) : (
        <QueueItem
          key={current.id}
          id={current.id}
          position={safeIndex + 1}
          count={ordered.length}
          onDecided={onDecided}
          onSkip={() => {
            setSkipped((s) => new Set(s).add(current.id));
          }}
          onPrev={() => setIndex((i) => Math.max(0, i - 1))}
          onNext={() => setIndex((i) => Math.min(ordered.length - 1, i + 1))}
        />
      )}
    </div>
  );
}

function QueueItem({
  id, position, count, onDecided, onSkip, onPrev, onNext,
}: {
  id: string; position: number; count: number;
  onDecided: (id: string, kind: 'approve' | 'reject') => void; onSkip: () => void; onPrev: () => void; onNext: () => void;
}) {
  const { data: l, isLoading, error } = useQuery({ queryKey: ['admin-listing', id], queryFn: () => api<Detail>(`/admin/listings/${id}`) });
  const [photo, setPhoto] = useState(0);
  const [zoom, setZoom] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('other');
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [help, setHelp] = useState(false);
  const commentRef = useRef<HTMLInputElement>(null);

  const decide = useCallback(
    async (kind: 'approve' | 'reject') => {
      if (busy || !l) return;
      if (l.status !== 'pending') {
        setErr(`Объявление уже ${STATUS_RU[l.status]?.toLowerCase() ?? l.status} — пропускаю`);
        onDecided(id, kind);
        return;
      }
      setBusy(true);
      setErr(null);
      try {
        await api(`/admin/listings/${id}/${kind}`, { method: 'POST', json: kind === 'approve' ? {} : { reasonCode: reason, reasonText: comment.trim() || undefined } });
        onDecided(id, kind);
      } catch (e) {
        setErr(errorText(e));
        setBusy(false);
      }
    },
    [busy, l, id, reason, comment, onDecided],
  );

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (zoom) {
        if (e.key === 'Escape' || e.key.toLowerCase() === 'f') setZoom(false);
        return;
      }
      if (help) {
        if (e.key === 'Escape' || e.key === '?') setHelp(false);
        return;
      }
      if (rejecting) {
        if (e.key === 'Escape') { e.preventDefault(); setRejecting(false); return; }
        if (e.key === 'Enter') { e.preventDefault(); void decide('reject'); return; }
        if (!isTyping(e.target) && /^[1-9]$/.test(e.key)) {
          const k = REASON_KEYS[Number(e.key) - 1];
          if (k) { e.preventDefault(); setReason(k); }
        }
        return;
      }
      if (isTyping(e.target)) return;
      const k = e.key;
      if (k === 'a' || k === 'A' || k === 'ф' || k === 'Ф') { e.preventDefault(); void decide('approve'); }
      else if (k === 'r' || k === 'R' || k === 'к' || k === 'К') { e.preventDefault(); setRejecting(true); setTimeout(() => commentRef.current?.focus(), 0); }
      else if (k === 's' || k === 'S' || k === 'ы' || k === 'Ы' || k === 'ArrowRight' || k === 'j') { e.preventDefault(); if (k === 'ArrowRight' || k === 'j') onNext(); else onSkip(); }
      else if (k === 'ArrowLeft' || k === 'k') { e.preventDefault(); onPrev(); }
      else if (k === ']' || k === 'ъ') setPhoto((p) => (l?.images.length ? (p + 1) % l.images.length : 0));
      else if (k === '[' || k === 'х') setPhoto((p) => (l?.images.length ? (p - 1 + l.images.length) % l.images.length : 0));
      else if ((k === 'f' || k === 'F' || k === 'а') && l?.images.length) setZoom(true);
      else if (k === '?') setHelp(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [zoom, help, rejecting, decide, onSkip, onPrev, onNext, l]);

  if (isLoading) return <Loading />;
  if (error || !l) return <ErrorBox error={error} />;
  const img = l.images[photo];

  return (
    <div className="queue" data-testid="queue-item">
      <div className="queue-media">
        <div className="photo-main" onClick={() => img && setZoom(true)} role="button" tabIndex={-1} aria-label="Фото на весь экран (F)">
          {img ? <img src={img.url} alt={`Фото ${photo + 1} из ${l.images.length}`} /> : <span className="muted">Без фото</span>}
          {l.images.length ? <span className="photo-count">{photo + 1} / {l.images.length}</span> : null}
        </div>
        {l.images.length > 1 ? (
          <div className="photo-strip">
            {l.images.map((i, n) => (
              <button key={i.id} type="button" aria-current={n === photo} aria-label={`Фото ${n + 1}`} onClick={() => setPhoto(n)}>
                <img src={i.thumbUrl ?? i.url} alt="" />
              </button>
            ))}
          </div>
        ) : null}
      </div>

      <div className="queue-side">
        <div className="panel">
          <div className="muted small">
            #{l.id} · {position} из {count} · {l.category} · {l.location}
          </div>
          <h2 className="queue-title">{l.title || '(без названия)'}</h2>
          <div className="queue-price">{l.price.formatted || '—'}</div>
          {l.condition ? <span className="pill">{l.condition === 'new' ? 'Новое' : 'Б/у'}</span> : null}
          <p className="pre" style={{ margin: '10px 0' }}>{l.description}</p>
          {l.attributes.length ? (
            <ul style={{ margin: 0, paddingLeft: 18 }}>
              {l.attributes.map((a) => <li key={a.key}>{a.label}: <strong>{String(a.value)}</strong></li>)}
            </ul>
          ) : null}
          <p className="muted small" style={{ marginBottom: 0 }}>
            Продавец: {l.user.firstName} {l.user.username ? `@${l.user.username}` : ''} (#{l.user.id})
            {l.history[0] ? ` · подано ${fmtDate(l.history.find((h) => h.action.includes('submit'))?.at ?? l.history[0].at)}` : ''}
          </p>
        </div>

        <SignalList flags={l.flags} signals={l.signals} />
        {l.reports.length ? <div className="signal danger"><Icon name="flag" size={16} />Жалоб на объявление: {l.reports.length}</div> : null}

        <div className="decide-bar" aria-label="Решение">
          {rejecting ? (
            <>
              <div className="reasons" role="group" aria-label="Причина отклонения">
                {REASON_KEYS.map((k, n) => (
                  <button key={k} type="button" aria-pressed={reason === k} onClick={() => setReason(k)}>
                    <kbd>{n + 1}</kbd>
                    {REJECT_REASONS[k]}
                  </button>
                ))}
              </div>
              <input
                ref={commentRef}
                placeholder="Комментарий продавцу (необязательно), Enter — отправить"
                maxLength={500}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                aria-label="Комментарий продавцу"
              />
              <div className="decide-row">
                <Btn kind="danger" busy={busy} onClick={() => void decide('reject')}>
                  <Icon name="x" size={16} /> Отклонить: {REJECT_REASONS[reason]} <kbd>Enter</kbd>
                </Btn>
                <Btn kind="secondary" onClick={() => setRejecting(false)}>
                  Отмена <kbd>Esc</kbd>
                </Btn>
              </div>
            </>
          ) : (
            <div className="decide-row">
              <Btn busy={busy} onClick={() => void decide('approve')}>
                <Icon name="check" size={16} /> Одобрить <kbd>A</kbd>
              </Btn>
              <Btn kind="danger" onClick={() => { setRejecting(true); setTimeout(() => commentRef.current?.focus(), 0); }}>
                <Icon name="x" size={16} /> Отклонить <kbd>R</kbd>
              </Btn>
              <Btn kind="secondary" onClick={onSkip}>
                <Icon name="skip" size={16} /> Пропустить <kbd>S</kbd>
              </Btn>
              <button type="button" className="icon-btn" aria-label="Горячие клавиши" title="Горячие клавиши (?)" onClick={() => setHelp(true)}>
                <Icon name="keyboard" size={16} />
              </button>
            </div>
          )}
          {err ? <div className="error-box" role="alert">{err}</div> : null}
        </div>
      </div>

      {zoom && img ? (
        <div className="zoom" onClick={() => setZoom(false)} role="dialog" aria-label="Фото">
          <img src={img.url} alt="" />
        </div>
      ) : null}
      {help ? (
        <Modal title="Горячие клавиши" onClose={() => setHelp(false)}>
          <div className="help-grid">
            <kbd>A</kbd><span>Одобрить</span>
            <kbd>R</kbd><span>Отклонить → <kbd>1</kbd>–<kbd>{REASON_KEYS.length}</kbd> причина, <kbd>Enter</kbd> отправить, <kbd>Esc</kbd> отмена</span>
            <kbd>S</kbd><span>Пропустить (в конец очереди)</span>
            <kbd>→</kbd><span>Следующее · <kbd>←</kbd> предыдущее</span>
            <kbd>[</kbd><span><kbd>]</kbd> листать фото · <kbd>F</kbd> фото на весь экран</span>
            <kbd>Ctrl K</kbd><span>Быстрый переход по разделам</span>
          </div>
          <p className="muted small">Работает и в русской раскладке.</p>
        </Modal>
      ) : null}
    </div>
  );
}

